import React, {forwardRef, useImperativeHandle, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {Form, Input, Modal, Select, Radio} from 'antd';

import {useJobInfo} from '@/page/admin/JobsPage/jobService';
import {addJob, updateJob} from '@/api/jobs';

const {Option} = Select;

//错误策略
const misfirePolicyOptions = [
    {label: '立即执行', value: '1'},
    {label: '执行一次', value: '2'},
    {label: '放弃执行', value: '3'},
];

//状态
const statusOptions = [
    {label: '正常', value: '0'},
    {label: '暂停', value: '1'},
];

const JobFormModal = forwardRef(({modalTitle, reload}, ref) => {
    const formRef = useRef();

    const [visible, setVisible] = useState(false);
    const [submiting, setSubmiting] = useState(false);

    const [jobId, setJobId] = useState(null);
    const {loading, value} = useJobInfo(jobId);

    function isAdd() {
        return !jobId;
    }

    useEffect(() => {
        setSubmiting(loading);

        if (loading) {
            return;
        }

        const jobData = _.get(value, 'data', {});
        if (_.isEmpty(jobData)) {
            return;
        }
        const {
            jobName,
            jobGroup,
            invokeTarget,
            jobId,
            cronExpression,
            concurrent,
            misfirePolicy,
            status,
            smsUser,
            mailUser,
            remark,
            env,
            ...rest
        } = jobData;
        formRef.current.setFieldsValue({
            jobName,
            jobGroup,
            invokeTarget,
            jobId,
            cronExpression,
            concurrent,
            misfirePolicy,
            status,
            smsUser,
            mailUser,
            env,
            remark,
        });
    }, [loading, value]);

    function showModal(jobId) {
        setVisible(true);
        setJobId(jobId);
    }

    function closeModal() {
        setVisible(false);
        setSubmiting(false);
        setJobId(null);
    }

    useImperativeHandle(ref, () => ({
        showModal,
        closeModal,
    }));

    function onFinish(values) {
        const {
            jobName,
            jobGroup,
            invokeTarget,
            cronExpression,
            concurrent,
            misfirePolicy,
            status,
            smsUser,
            mailUser,
            remark,
            env,
        } = values;

        const jobData = {
            jobName,
            jobGroup,
            invokeTarget,
            cronExpression,
            concurrent,
            misfirePolicy,
            status,
            smsUser,
            mailUser,
            remark,
            env,
        };

        setSubmiting(true);
        const promise = isAdd() ? addJob(jobData) : updateJob(jobId, jobData);
        promise
            .then(data => {
                if (typeof data.success !== 'undefined' && data.success) {
                    reload();
                    closeModal();
                } else {
                    console.log('job add', data);
                }
            })
            .finally(() => {
                setSubmiting(false);
            });
    }

    function onOk() {
        formRef.current.submit();
    }

    return (
        <Modal
            destroyOnClose
            maskClosable={false}
            width={680}
            title={isAdd() ? '添加定时任务' : '修改定时任务'}
            visible={visible}
            onCancel={closeModal}
            onOk={onOk}
            okButtonProps={{loading: submiting}}
        >
            <Form ref={formRef} onFinish={onFinish} layout="vertical">
                <Form.Item
                    name="jobName"
                    label="任务名称"
                    rules={
                        isAdd()
                            ? [
                                  {
                                      required: true,
                                      max: 100,
                                  },
                              ]
                            : null
                    }
                >
                    <Input placeholder="任务名称" disabled={!isAdd()} />
                </Form.Item>
                <Form.Item label="任务组名" name="jobGroup" initialValue="DEFAULT">
                    <Select>
                        <Option value="DEFAULT">默认</Option>
                        <Option value="SYSTEM">系统</Option>
                        <Option value="MEDIA">图文</Option>
                    </Select>
                </Form.Item>
                <Form.Item
                    name="invokeTarget"
                    label="调用方法"
                    tooltip="Bean调用示例：naviTask.naviParams('navi')"
                    rules={
                        isAdd()
                            ? [
                                  {
                                      required: true,
                                      max: 200,
                                  },
                              ]
                            : null
                    }
                >
                    <Input placeholder="调用方法" disabled={!isAdd()} />
                </Form.Item>
                <Form.Item
                    name="cronExpression"
                    label={
                        <div>
                            cron表达式
                            <span
                                style={{
                                    color: 'red',
                                    fontSize: 'smaller',
                                    fontWeight: 'bold',
                                }}
                            >
                                「请勿修改」
                            </span>
                        </div>
                    }
                    rules={[
                        {
                            required: true,
                        },
                    ]}
                >
                    <Input placeholder="cron表达式" />
                </Form.Item>
                <Form.Item name="concurrent" label="是否并发" initialValue="1">
                    <Radio.Group buttonStyle="solid">
                        <Radio.Button value="0">允许</Radio.Button>
                        <Radio.Button value="1">禁止</Radio.Button>
                    </Radio.Group>
                </Form.Item>
                <Form.Item
                    label="错误策略"
                    name="misfirePolicy"
                    tooltip="立即执行（所有misfire的任务会马上执行）执行一次（会合并部分的misfire，正常执行下一个周期的任务）放弃执行(所有的misfire不管，执行下一个周期的任务)"
                    initialValue="3"
                >
                    <Radio.Group
                        options={misfirePolicyOptions}
                        optionType="button"
                        buttonStyle="solid"
                    />
                </Form.Item>

                <Form.Item
                    label="状态"
                    name="status"
                    tooltip="首次添加，默认为暂停状态"
                    initialValue="1"
                >
                    <Radio.Group options={statusOptions} />
                </Form.Item>
                <Form.Item name="smsUser" label="短信报警通知人" tooltip="多个通知人以英文逗号分隔">
                    <Input placeholder="短信报警通知人" />
                </Form.Item>
                <Form.Item
                    name="mailUser"
                    label="邮件报警通知人"
                    tooltip="多个通知人以英文逗号分隔"
                >
                    <Input placeholder="邮件报警通知人" />
                </Form.Item>
                <Form.Item
                    name="remark"
                    label="备注"
                    rules={[
                        {
                            max: 200,
                        },
                    ]}
                >
                    <Input placeholder="备注" />
                </Form.Item>
                <Form.Item
                    name="env"
                    label={
                        <div>
                            环境标识
                            <span
                                style={{
                                    color: 'red',
                                    fontSize: 'smaller',
                                    fontWeight: 'bold',
                                }}
                            >
                                「请勿随便修改」
                            </span>
                        </div>
                    }
                    tooltip="0是测试，1是生产"
                >
                    <Input placeholder="请输入环境标识" />
                </Form.Item>
            </Form>
        </Modal>
    );
});

JobFormModal.displayName = 'JobFormModal';

JobFormModal.propTypes = {
    modalTitle: PropTypes.string,
    reload: PropTypes.func,
};

export default JobFormModal;
